import React, { useState, useEffect } from 'react';
import { useLanguage, api } from '@/lib/context';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Crown, Clock, CreditCard, Loader2, CheckCircle2, 
  AlertTriangle, Zap, TrendingUp, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export const SubscriptionStatus = ({ onUpgrade = null }) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [plans, setPlans] = useState([]);
  const [showPlansDialog, setShowPlansDialog] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [subRes, usageRes, plansRes] = await Promise.all([
        api.getSubscription().catch(() => ({ data: null })),
        api.getUsageMetrics('current_month').catch(() => ({ data: {} })),
        api.getSubscriptionPlans().catch(() => ({ data: [] }))
      ]);
      setSubscription(subRes.data);
      setUsage(usageRes.data);
      setPlans(plansRes.data || []);
    } catch (error) {
      console.error('Failed to fetch subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    setSubscribing(true);
    try {
      await api.createMonetizationSubscription({ plan_id: planId });
      toast.success(t('subscription.activated'));
      fetchData();
      setShowPlansDialog(false);
      if (onUpgrade) onUpgrade();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setSubscribing(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm(t('subscription.confirmCancel'))) return;
    
    try {
      await api.cancelSubscription();
      toast.success(t('subscription.cancelled'));
      fetchData();
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  // No subscription - show trial/upgrade prompt
  if (!subscription) {
    return (
      <>
        <Card className="border-brand/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-brand" />
              {t('subscription.noSubscription')}
            </CardTitle>
            <CardDescription>
              {t('subscription.startTrial')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-brand/5 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-brand" />
                <span className="text-sm">{t('subscription.trialFeature1')}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-brand" />
                <span className="text-sm">{t('subscription.trialFeature2')}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-brand" />
                <span className="text-sm">{t('subscription.trialFeature3')}</span>
              </div>
            </div>
          </CardContent>
          <CardFooter>
            <Button onClick={() => setShowPlansDialog(true)} className="w-full gap-2">
              <Zap className="h-4 w-4" />
              {t('subscription.startFreeTrial')}
            </Button>
          </CardFooter>
        </Card>
        <PlansDialog 
          open={showPlansDialog} 
          onClose={() => setShowPlansDialog(false)}
          plans={plans}
          onSelect={handleSubscribe}
          subscribing={subscribing}
        />
      </>
    );
  }

  // Has subscription - show status
  const isTrial = subscription.is_trial;
  const isExpired = subscription.status === 'expired';
  const daysRemaining = subscription.trial_ends_at 
    ? Math.max(0, Math.ceil((new Date(subscription.trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;
  
  const unlocksUsedPercent = subscription.unlocks_limit > 0 
    ? (subscription.unlocks_used / subscription.unlocks_limit) * 100 
    : 0;
  const listingsUsedPercent = subscription.listings_limit > 0 
    ? (subscription.listings_used / subscription.listings_limit) * 100 
    : 0;

  return (
    <>
      <Card className={isExpired ? 'border-red-500/50' : isTrial ? 'border-amber-500/50' : 'border-green-500/50'}>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Crown className={`h-5 w-5 ${isExpired ? 'text-red-500' : isTrial ? 'text-amber-500' : 'text-green-500'}`} />
                {subscription.plan_name}
              </CardTitle>
              <CardDescription className="flex items-center gap-2 mt-1">
                <Badge variant={isExpired ? 'destructive' : isTrial ? 'secondary' : 'default'}>
                  {isExpired ? (t('subscription.expired')) :
                   isTrial ? (t('subscription.trial')) :
                   (t('subscription.active'))}
                </Badge>
                {!isExpired && (
                  <span className="text-xs">
                    €{subscription.price_monthly}/{t('subscription.month')}
                  </span>
                )}
              </CardDescription>
            </div>
            {isTrial && daysRemaining !== null && (
              <Badge variant="outline" className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {daysRemaining} {t('subscription.daysLeft')}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Trial warning */}
          {isTrial && daysRemaining !== null && daysRemaining <= 7 && (
            <div className="bg-amber-50 dark:bg-amber-950/30 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700 dark:text-amber-400">
                {t('subscription.trialExpiringSoon') || `Your trial expires in ${daysRemaining} days. Upgrade to continue.`}
              </p>
            </div>
          )}

          {/* Expired warning */}
          {isExpired && (
            <div className="bg-red-50 dark:bg-red-950/30 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 dark:text-red-400">
                {t('subscription.expiredWarning')}
              </p>
            </div>
          )}

          {/* Usage metrics */}
          {!isExpired && (
            <div className="space-y-3">
              {/* Unlocks */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>{t('subscription.unlocks')}</span>
                  <span>
                    {subscription.unlocks_used} / {subscription.unlocks_limit === 0 ? '∞' : subscription.unlocks_limit}
                  </span>
                </div>
                {subscription.unlocks_limit > 0 && (
                  <Progress value={unlocksUsedPercent} className="h-2" />
                )}
              </div>

              {/* Listings */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>{t('subscription.listings')}</span>
                  <span>
                    {subscription.listings_used} / {subscription.listings_limit === 0 ? '∞' : subscription.listings_limit}
                  </span>
                </div>
                {subscription.listings_limit > 0 && (
                  <Progress value={listingsUsedPercent} className="h-2" />
                )}
              </div>
            </div>
          )}

          {/* Upgrade recommendation */}
          {usage?.upgrade_recommended && (
            <div className="bg-brand/5 rounded-lg p-3 flex items-start gap-2">
              <TrendingUp className="h-4 w-4 text-brand flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-brand">
                  {t('subscription.upgradeRecommended')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('subscription.upgradeRecommendedDesc')}
                </p>
              </div>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex gap-2">
          {isExpired || isTrial ? (
            <Button onClick={() => setShowPlansDialog(true)} className="flex-1 gap-2">
              <Zap className="h-4 w-4" />
              {isExpired ? (t('subscription.resubscribe')) : (t('subscription.upgrade'))}
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => setShowPlansDialog(true)} className="flex-1">
                {t('subscription.changePlan')}
              </Button>
              <Button variant="ghost" onClick={handleCancel}>
                {t('subscription.cancel')}
              </Button>
            </>
          )}
        </CardFooter>
      </Card>
      <PlansDialog 
        open={showPlansDialog} 
        onClose={() => setShowPlansDialog(false)}
        plans={plans}
        onSelect={handleSubscribe}
        subscribing={subscribing}
        currentPlan={subscription?.plan_id}
      />
    </>
  );
};

// Plans dialog component
const PlansDialog = ({ open, onClose, plans, onSelect, subscribing, currentPlan }) => {
  const { t } = useLanguage();
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('subscription.choosePlan')}</DialogTitle>
          <DialogDescription>
            {t('subscription.choosePlanDesc')}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4 md:grid-cols-3">
          {plans.map((plan) => (
            <Card 
              key={plan.id} 
              className={`cursor-pointer transition-all hover:border-brand ${currentPlan === plan.id ? 'border-brand ring-2 ring-brand/20' : ''}`}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">{plan.name}</CardTitle>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold">€{plan.price_monthly}</span>
                  <span className="text-sm text-muted-foreground">/{t('subscription.month')}</span>
                </div>
              </CardHeader>
              <CardContent className="pb-2">
                <ul className="space-y-1 text-sm">
                  {plan.features?.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <CheckCircle2 className="h-3 w-3 text-green-500" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter>
                <Button 
                  variant={currentPlan === plan.id ? 'secondary' : 'default'}
                  className="w-full"
                  onClick={() => onSelect(plan.id)}
                  disabled={subscribing || currentPlan === plan.id}
                >
                  {subscribing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : currentPlan === plan.id ? (
                    t('subscription.currentPlan')
                  ) : plan.trial_days > 0 ? (
                    t('subscription.startTrial')
                  ) : (
                    t('subscription.select')
                  )}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionStatus;
